/**
 * auditUtils.js
 * Shared utility for creating audit log entries.
 * Matches existing pattern in DrillsModule.js captureChanges().
 * Part of FT-06c: Edit Audit Trail
 */

/**
 * Create an audit log entry by diffing old and new objects.
 * @param {Object} oldEntry - Original entry before edit
 * @param {Object} newEntry - Entry after edit
 * @param {Object} currentUser - From usePermissions(): { id, name, role }
 * @param {Object} options - { reason?: string, action?: string, fieldsToTrack?: string[] }
 * @returns {Object|null} Audit log entry, or null if no changes detected
 */
export const createAuditEntry = (oldEntry, newEntry, currentUser, options = {}) => {
  const { reason, action = 'updated', fieldsToTrack } = options;

  // Fields to ignore when diffing (metadata, not user data)
  const ignoreFields = ['updatedAt', 'updatedBy', 'lastModified', 'auditLog', 'editHistory', 'createdAt', 'createdBy'];

  // Determine which fields to compare
  const keysToCheck = fieldsToTrack
    ? fieldsToTrack
    : [...new Set([...Object.keys(oldEntry || {}), ...Object.keys(newEntry || {})])];

  const changes = [];
  for (const field of keysToCheck) {
    if (ignoreFields.includes(field)) continue;
    const oldValue = oldEntry?.[field];
    const newValue = newEntry?.[field];
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes.push({ field, oldValue: formatValue(oldValue), newValue: formatValue(newValue) });
    }
  }

  if (changes.length === 0) return null;

  const summary = changes.length === 1
    ? `${changes[0].field} updated`
    : `${changes.length} fields updated`;

  return {
    id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    timestamp: new Date().toISOString(),
    user: currentUser?.name || 'Unknown',
    userId: currentUser?.id,
    role: currentUser?.role,
    action,
    summary,
    changes,
    ...(reason && { reason })
  };
};

/**
 * Append an audit entry to an existing audit log array (immutable).
 * @param {Array} existingLog - Existing auditLog array (may be undefined)
 * @param {Object} auditEntry - Entry from createAuditEntry()
 * @returns {Array} New audit log array with entry appended
 */
export const appendToAuditLog = (existingLog, auditEntry) => {
  if (!auditEntry) return existingLog || [];
  return [...(existingLog || []), auditEntry];
};

/** Format a value for display in audit log (truncate long strings, stringify objects) */
const formatValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') {
    const str = JSON.stringify(value);
    return str.length > 200 ? str.substring(0, 200) + '...' : str;
  }
  if (typeof value === 'string' && value.length > 200) {
    return value.substring(0, 200) + '...';
  }
  return value;
};
